import type { SegmentCondition, SegmentRules } from "./audience_helper";
import type { ContactFieldsQueryValue, FieldFilterOp } from "./fieldFilters";

/** Converte payload de campo (lista/disparo) em condição de segmento com operador. */
function fieldConditionFromQueryValue(
  slug: string,
  raw: ContactFieldsQueryValue
): SegmentCondition | null {
  if (typeof raw === "string") {
    const value = raw.trim();
    if (value === "") {
      return null;
    }
    return { type: "field", slug, op: "eq", value };
  }

  if (!raw || typeof raw !== "object" || !("op" in raw)) {
    return null;
  }

  const op = raw.op as FieldFilterOp;

  if (op === "in") {
    const values = Array.isArray(raw.value)
      ? raw.value.map((v) => String(v).trim()).filter(Boolean)
      : String(raw.value || "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
    if (values.length === 0) {
      return null;
    }
    return { type: "field", slug, op: "in", value: values.join(",") };
  }

  if (op === "between") {
    const from = String(raw.value ?? "").trim();
    const to = String(raw.value_to ?? "").trim();
    if (from === "" || to === "") {
      return null;
    }
    return { type: "field", slug, op: "between", value: from, value_to: to };
  }

  const value = String(raw.value ?? "").trim();
  if (value === "") {
    return null;
  }

  return { type: "field", slug, op, value };
}

/**
 * Audiência tipada do disparo (mode=filter) — status, tags e campos com operadores.
 */
export function buildSegmentRulesFromAudienceFilter(input: {
  status?: string;
  tag_ids?: number[];
  tag_match?: "and" | "or";
  fields?: Record<string, ContactFieldsQueryValue>;
}): SegmentRules | null {
  const conditions: SegmentCondition[] = [];

  const status = (input.status || "").trim();
  if (status) {
    conditions.push({ type: "status", op: "eq", value: status });
  }

  const tagIds = (input.tag_ids || []).filter((id) => id > 0);
  if (tagIds.length > 0) {
    conditions.push({
      type: "tag",
      op: "in",
      tag_ids: tagIds,
      tag_match: input.tag_match === "and" ? "and" : "or",
    });
  }

  const fields = input.fields || {};
  for (const [slug, raw] of Object.entries(fields)) {
    if (!slug) {
      continue;
    }
    const condition = fieldConditionFromQueryValue(slug, raw);
    if (condition) {
      conditions.push(condition);
    }
  }

  if (conditions.length === 0) {
    return null;
  }

  return {
    version: 1,
    match: "all",
    conditions,
  };
}

/**
 * Monta rules v1 a partir dos filtros da lista de Contatos (DOC 03 § 4.8.1).
 * Fora do contrato: search, phone, trashed — não entram nas condições.
 */
export function buildSegmentRulesFromContactFilters(input: {
  status?: string;
  tagIds?: number[];
  tagMatch?: "and" | "or";
  fields?: Record<string, ContactFieldsQueryValue>;
}): SegmentRules | null {
  return buildSegmentRulesFromAudienceFilter({
    status: input.status,
    tag_ids: input.tagIds,
    tag_match: input.tagMatch,
    fields: input.fields,
  });
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Resumo leigo de uma condição (lista / modal / disparo). */
export function describeSegmentCondition(
  condition: SegmentCondition,
  t: Translate,
  extras?: {
    tagNames?: Record<number, string>;
    fieldLabels?: Record<string, string>;
  }
): string {
  if (condition.type === "status") {
    const label = t(`segments.status.${condition.value}`, {
      defaultValue: condition.value,
    });
    return t("segments.describe.status", { status: label });
  }

  if (condition.type === "tag") {
    const names = condition.tag_ids.map(
      (id) => extras?.tagNames?.[id] || `#${id}`
    );
    const joined = names.join(
      condition.tag_match === "and"
        ? t("segments.describe.andJoin")
        : t("segments.describe.orJoin")
    );
    if (condition.op === "not_in") {
      return t("segments.describe.tagNotIn", { tags: joined });
    }
    return t("segments.describe.tagIn", { tags: joined });
  }

  if (condition.type === "field") {
    const label =
      extras?.fieldLabels?.[condition.slug] || condition.slug;
    return t("segments.describe.field", {
      field: label,
      value: String(condition.value),
    });
  }

  const eventLabel = t(`segments.event.${condition.event}`);
  if (condition.op === "not_in_last_days") {
    return t("segments.describe.engagementNot", {
      event: eventLabel,
      days: condition.days,
    });
  }
  return t("segments.describe.engagement", {
    event: eventLabel,
    days: condition.days,
  });
}

export function describeSegmentRules(
  rules: SegmentRules | null | undefined,
  t: Translate,
  extras?: {
    tagNames?: Record<number, string>;
    fieldLabels?: Record<string, string>;
  }
): string {
  if (!rules?.conditions?.length) {
    return t("segments.describe.empty");
  }
  const parts = rules.conditions.map((c) =>
    describeSegmentCondition(c, t, extras)
  );
  const join =
    rules.match === "any"
      ? t("segments.describe.orJoin")
      : t("segments.describe.andJoin");
  return parts.join(join);
}
